import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Inbox, Loader2, RefreshCw } from "lucide-react";
import { API } from "@/lib/api";

/**
 * FranchiseTicketsInbox — owner-only tab that lists every ticket
 * submitted with source="franchise" (from Admin → Franchise → Contact
 * Link Ride support). Lets the owner triage without hunting through
 * email. Mark resolved with a click, add an internal note, expand to
 * see the full message.
 */
function FilterChip({ value, label, count, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-[#0047FF] border-[#0047FF] text-white"
          : "bg-white/5 border-white/10 text-zinc-300 hover:border-white/30"
      }`}
      data-testid={`franchise-tickets-filter-${value}`}
    >
      {label}
      {count !== undefined && (
        <span className={`ml-1.5 ${active ? "opacity-80" : "text-zinc-500"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

export default function FranchiseTicketsInbox() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0 });
  const [filter, setFilter] = useState("open"); // open | resolved | all
  const [expandedId, setExpandedId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [replyDraft, setReplyDraft] = useState({});
  const [replyingId, setReplyingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await axios.get(`${API}/admin/franchise/tickets`, { params });
      setTickets(r.data?.tickets || []);
      setCounts(r.data?.counts || { open: 0, resolved: 0 });
    } catch (e) {
      toast.error("Couldn't load tickets");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (ticket, nextStatus) => {
    setSavingId(ticket.id);
    try {
      await axios.patch(`${API}/admin/support/tickets/${ticket.id}`, {
        status: nextStatus,
        admin_note: noteDraft[ticket.id] ?? ticket.admin_note ?? "",
      });
      toast.success(nextStatus === "resolved" ? "Marked resolved" : "Reopened");
      await load();
    } catch (e) {
      toast.error("Couldn't update the ticket");
    } finally {
      setSavingId(null);
    }
  };

  const sendReply = async (ticket) => {
    const msg = (replyDraft[ticket.id] || "").trim();
    if (msg.length < 2) return toast.error("Type a reply first");
    setReplyingId(ticket.id);
    try {
      await axios.post(`${API}/admin/support/tickets/${ticket.id}/reply`, { message: msg });
      toast.success(ticket.customer_email ? `Reply emailed to ${ticket.customer_email}` : "Reply saved");
      setReplyDraft((prev) => ({ ...prev, [ticket.id]: "" }));
      await load();
    } catch (e) {
      toast.error("Couldn't send the reply");
    } finally {
      setReplyingId(null);
    }
  };

  return (
    <Card className="bg-[#121212] border-white/5" data-testid="franchise-tickets-inbox">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base font-medium text-white flex items-center gap-2">
            <Inbox className="w-4 h-4 text-[#4D88FF]" />
            Franchise tickets
          </CardTitle>
          <div className="flex items-center gap-2">
            <FilterChip value="open" label="Open" count={counts.open} active={filter === "open"} onSelect={setFilter} />
            <FilterChip value="resolved" label="Resolved" count={counts.resolved} active={filter === "resolved"} onSelect={setFilter} />
            <FilterChip value="all" label="All" active={filter === "all"} onSelect={setFilter} />
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              className="h-8 px-2 text-zinc-400 hover:text-white"
              data-testid="franchise-tickets-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="py-6 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center" data-testid="franchise-tickets-empty">
            No franchise tickets in this view.
          </p>
        ) : (
          tickets.map((t) => {
            const opened = expandedId === t.id;
            const ageDays = t.created_at
              ? Math.max(0, Math.floor((Date.now() - new Date(t.created_at).getTime()) / 86400000))
              : null;
            return (
              <div
                key={t.id}
                className="rounded-lg border border-white/5 bg-white/[0.02] p-3"
                data-testid={`franchise-ticket-${t.id}`}
              >
                <button
                  type="button"
                  className="w-full flex items-start justify-between gap-2 text-left"
                  onClick={() => setExpandedId(opened ? null : t.id)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">{t.subject}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {t.tenant_id} · {t.customer_email || "no email"} · {ageDays != null ? `${ageDays}d ago` : ""}
                    </p>
                  </div>
                  <Badge
                    className={
                      t.status === "resolved"
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                        : "bg-amber-500/15 text-amber-300 border-amber-500/30"
                    }
                  >
                    {t.status}
                  </Badge>
                </button>

                {opened && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
                    <p
                      className="text-sm text-zinc-200 whitespace-pre-wrap"
                      data-testid={`franchise-ticket-msg-${t.id}`}
                    >
                      {t.message}
                    </p>

                    {/* Reply thread — shows every admin reply chronologically. */}
                    {Array.isArray(t.replies) && t.replies.length > 0 && (
                      <div className="space-y-2 pl-3 border-l-2 border-[#4D88FF]/30">
                        {t.replies.map((r) => (
                          <div key={r.id} className="text-xs text-zinc-300">
                            <p className="text-[10px] uppercase tracking-wider text-[#80B0FF] font-semibold">
                              {r.author_name || "Support"} · {new Date(r.created_at).toLocaleString()}
                            </p>
                            <p className="text-sm text-zinc-200 whitespace-pre-wrap mt-0.5">{r.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply composer — emails customer_email directly if present. */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                        Reply to submitter{t.customer_email ? ` (emails ${t.customer_email})` : " (no email on file — reply saved on ticket only)"}
                      </p>
                      <Textarea
                        rows={2}
                        placeholder="Type your reply…"
                        value={replyDraft[t.id] || ""}
                        onChange={(e) => setReplyDraft((prev) => ({ ...prev, [t.id]: e.target.value }))}
                        className="bg-black border-white/10 text-sm"
                        data-testid={`franchise-ticket-reply-${t.id}`}
                      />
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => sendReply(t)}
                          disabled={replyingId === t.id || (replyDraft[t.id] || "").trim().length < 2}
                          className="bg-[#0047FF] hover:bg-[#003BE0] text-white"
                          data-testid={`franchise-ticket-reply-send-${t.id}`}
                        >
                          {replyingId === t.id ? "Sending…" : "Send reply"}
                        </Button>
                      </div>
                    </div>

                    <Textarea
                      rows={2}
                      placeholder="Internal note (only you see this)…"
                      value={noteDraft[t.id] ?? t.admin_note ?? ""}
                      onChange={(e) => setNoteDraft((prev) => ({ ...prev, [t.id]: e.target.value }))}
                      className="bg-black border-white/10 text-sm"
                      data-testid={`franchise-ticket-note-${t.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      {t.status === "open" ? (
                        <Button
                          size="sm"
                          onClick={() => resolve(t, "resolved")}
                          disabled={savingId === t.id}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white"
                          data-testid={`franchise-ticket-resolve-${t.id}`}
                        >
                          Mark resolved
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve(t, "open")}
                          disabled={savingId === t.id}
                          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                          data-testid={`franchise-ticket-reopen-${t.id}`}
                        >
                          Reopen
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
